import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Sun, Menu, X, Home, MessageCircle, User, BookOpen, Sparkles, LogOut } from 'lucide-react';
import SoulBalmLogo from './SoulBalmLogo';

export default function Navigation() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  if (!user) return null;

  const navItems = [
    { path: '/home', icon: Sparkles, label: 'Reading', mobileLabel: 'Reading' },
    { path: '/daily-energy', icon: Sun, label: 'Daily Energy', mobileLabel: 'Energy' },
    { path: '/chat', icon: MessageCircle, label: 'Talk to the Guide', mobileLabel: 'Guide' },
    { path: '/journal', icon: BookOpen, label: 'Journey', mobileLabel: 'Journey' },
    { path: '/profile', icon: User, label: 'Profile', mobileLabel: 'Profile' },
  ];

  const isActive = (path) => location.pathname === path;

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <>
      {/* Desktop Nav */}
      <nav data-testid="main-navigation" className="hidden md:flex fixed top-0 left-0 right-0 z-50 items-center justify-between px-8 py-3 backdrop-blur-2xl bg-[#070A1A]/60 border-b border-white/5">
        <Link to="/home" className="flex items-center gap-3" data-testid="nav-logo">
          <SoulBalmLogo size={32} />
          <span className="font-['Playfair_Display'] text-lg text-[#FFFFF0]">Tarot Room</span>
        </Link>
        <div className="flex items-center gap-1">
          {navItems.map(item => (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm tracking-wide transition-all duration-300 ${
                isActive(item.path)
                  ? 'bg-white/10 text-[#D4AF37] border border-[#D4AF37]/30'
                  : 'text-[#BDB2C4] hover:text-[#FFFFF0] hover:bg-white/5'
              }`}
            >
              <item.icon size={16} strokeWidth={1.5} />
              {item.label}
            </Link>
          ))}
        </div>
        <button onClick={handleLogout} data-testid="nav-logout-btn" className="text-sm text-[#BDB2C4] hover:text-[#C87886] transition-colors duration-300">
          Leave the Room
        </button>
      </nav>

      {/* Mobile Nav */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 backdrop-blur-2xl bg-[#070A1A]/80 border-t border-white/5 px-2 py-2">
        <div className="flex justify-around" data-testid="mobile-navigation">
          {navItems.map(item => (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`mobile-nav-${item.label.toLowerCase()}`}
              className={`flex flex-col items-center gap-1 px-2 py-1 rounded-lg transition-all duration-300 ${
                isActive(item.path) ? 'text-[#D4AF37]' : 'text-[#BDB2C4]'
              }`}
            >
              <item.icon size={20} strokeWidth={1.5} />
              <span className="text-[10px] tracking-wider">{item.mobileLabel}</span>
            </Link>
          ))}
          <button
            onClick={handleLogout}
            data-testid="mobile-logout-btn"
            className="flex flex-col items-center gap-1 px-2 py-1 rounded-lg transition-all duration-300 text-[#BDB2C4] hover:text-[#C87886]"
          >
            <LogOut size={20} strokeWidth={1.5} />
            <span className="text-[10px] tracking-wider">Logout</span>
          </button>
        </div>
      </div>

      {/* Spacers */}
      <div className="hidden md:block h-16" />
      <div className="md:hidden h-0" />
    </>
  );
}
